<script setup lang="ts">
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import MrTypography from '../mr-typography/MrTypography.vue';

interface IProps {
    title?: string;
    description?: string;
}

const { title } = defineProps<IProps>();

const expanded = defineModel<boolean>('expanded', { default: false });
</script>

<template>
    <div class="accordion-container">
        <button
            class="accordion"
            :class="{ active: expanded }"
            @click="expanded = !expanded"
        >
            <slot name="title" :title :expanded>
                <mr-typography
                    weight="500"
                    :class="{ 'title-expanded': expanded }"
                >
                    {{ title }}
                </mr-typography>
            </slot>
            <mr-icon
                :icon="expanded ? faChevronUp : faChevronDown"
                color="var(--blue-400)"
            />
        </button>
        <div class="panel" :class="{ expanded }">
            <div class="panel-inner">
                <mr-divider class="divider" :thickness="1" />
                <slot name="description">
                    <mr-typography class="content">
                        {{ description }}
                    </mr-typography>
                </slot>
            </div>
        </div>
        <slot name="separator" />
    </div>
</template>
<style scoped lang="scss">
.accordion :deep(.index) {
    display: none;
}

.accordion-container {
    background-color: white;
    padding-inline: 20px;
    border-radius: 12px;
    @include shadow();
}

.title-expanded {
    padding-bottom: 12px;
}

.accordion {
    all: unset;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: none;
    cursor: pointer;
    width: 100%;
    border: none;
    outline: none;
    transition: 0.4s;
    font-style: normal;
    @include font-weight(bold);
    font-size: 14px;
    line-height: 22px;
    text-align: right;
    color: usePalette($p-cool-gray, $p-800);
    @include set-font();
    position: relative;
}

.panel {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 250ms ease;
    text-align: justify;
    margin: 0;
    font-size: 14px;
    font-style: normal;
    @include font-weight();
    color: usePalette($p-cool-gray, $p-800);
    line-height: 22px;
    contain: layout style;
    .divider {
        padding-bottom: 12px;
    }
    &.expanded {
        grid-template-rows: 1fr;
    }
    .panel-inner {
        overflow: hidden;
    }
    .content {
        padding-bottom: 22px;
    }
}

@media (max-width: $app-mobile-max-width) {
    .accordion :deep(div) {
        margin-left: 35px;
    }
}
</style>
